import get from 'lodash-es/get';

import {
    IdentityBodyAssess, IdentityOrganization,
    IdentityOrganizationPerson,
    IdentityTeam,
    IdentityUser,
    Location,
    Person,
    Wrestler,
} from '@reg/shared/model';
import { IdentityPersonService } from '@reg/shared/service';
import { addMonths, addYears, format, getYear } from 'date-fns';
import { DateFormat } from '@reg/shared/enum';
import { convertToDateTime } from '@reg/shared/helper/dateTime-helper';

/**
 * Model to hold Person data from identity
 */
export class IdentityPerson extends Person {
    public static get GENDER_MALE(): string {
        return 'M';
    }

    public static get GENDER_FEMALE(): string {
        return 'F';
    }

    public firstName: string = null;
    public lastName: string = null;
    public email: string = null;
    public gender: string = null;
    public highSchoolGradYear: number;
    public usaWrestlingNumber: number = null;
    public trackwrestlingId: number = null;
    public skillLevelStars: number = null;
    public location: Location;
    public teams: IdentityTeam[] = [];
    public athletesAsParent: IdentityPerson[] = [];
    public parentsAsAthlete: IdentityPerson[] = [];
    public user: IdentityUser = null;
    public hasModifyAccess: boolean;
    public bodyAssessments: IdentityBodyAssess[] = [];
    public sport: any;
    public disableAutoMerge: boolean = false;

    public static getFields(): string[] {
        return [
            'firstName',
            'lastName',
            'email',
            'gender',
            'dateOfBirth',
            'age',
            'highSchoolGradYear',
            'sport',
            'location',
            'userId',
            'disableAutoMerge',
            'athletesAsParent',
            'parentsAsAthlete',
            IdentityPersonService.INCLUDE_TEAMS_AS_ATHLETE,
            IdentityPersonService.INCLUDE_BODY_ASSESSMENTS,
            IdentityPersonService.INCLUDE_USER,
            IdentityPersonService.INCLUDE_ORGANIZATION_PEOPLE,
        ];
    }

    public static calculateHSGraduationYear(dateOfBirth: Date): number {
        if (dateOfBirth) {
            return getYear(addMonths(addYears(dateOfBirth, 18), 5));
        }

        return null;
    }

    constructor(id: string = null) {
        super();
        this.id = id;
        this.location = new Location();
    }

    public formatNameFirstLast(): string {
        let name: string = this.firstName || '';

        if (this.lastName) {
            name += name ? ' ' : '';
            name += this.lastName;
        }

        return name;
    }

    public formatGender(): string {
        if ((this.gender || '').toUpperCase() === IdentityPerson.GENDER_MALE) {
            return 'Male';
        } if ((this.gender || '').toUpperCase() === IdentityPerson.GENDER_FEMALE) {
            return 'Female';
        }

        return '';
    }

    public formatNameLastFirst(): string {
        let name: string = this.lastName || '';

        if (this.firstName) {
            name += name ? ', ' : '';
            name += this.firstName;
        }

        return name;
    }

    public formatNameFirstLastOrEmail(): string {
        return this.formatNameFirstLast() || this.email;
    }

    public fillFromWrestler(wrestler: Wrestler): IdentityPerson {
        this.id = wrestler.identityPersonId;
        this.firstName = wrestler.firstName;
        this.lastName = wrestler.lastName;

        // Only set the dob and hs grad year if we don't already have data for them
        this.dateOfBirth = this.dateOfBirth || wrestler.dateOfBirth;

        if (wrestler.grade && wrestler.grade.id) {
            this.highSchoolGradYear = wrestler.calculateHighSchoolGradYear();
        } else {
            this.highSchoolGradYear = this.highSchoolGradYear || wrestler.calculateHighSchoolGradYear();
        }

        if (wrestler.usaWrestlingCardNum &&
            this.usaWrestlingNumber !== wrestler.usaWrestlingCardNum) {
            this.usaWrestlingNumber = wrestler.usaWrestlingCardNum;
        }

        if (wrestler.location) {
            this.location = wrestler.location.clone();
        }

        if (wrestler.skillLevel &&
            wrestler.skillLevel.stars) {
            this.skillLevelStars = wrestler.skillLevel.stars;
        }

        if (wrestler.team &&
            wrestler.team.identityTeamId) {
            this.teams = this.teams || [];
            const identityTeam: IdentityTeam = this.teams
                .filter((x: IdentityTeam) => x.id === wrestler.team.identityTeamId)[0];

            if (!identityTeam) {
                const newIdentityTeam: IdentityTeam = new IdentityTeam();
                newIdentityTeam.id = wrestler.team.identityTeamId;
                newIdentityTeam.name = wrestler.team.name;
                this.teams.push(newIdentityTeam);
            }
        } else {
            const newIdentityTeam: IdentityTeam = new IdentityTeam();
            newIdentityTeam.name = wrestler.team.name;
            this.teams.push(newIdentityTeam);
        }

        return this;
    }

    /**
     * Clone Team object
     */
    public clone(): IdentityPerson {
        const clone: IdentityPerson = new IdentityPerson();
        clone.id = this.id;
        clone.firstName = this.firstName;
        clone.lastName = this.lastName;
        clone.email = this.email;
        clone.gender = this.gender;
        clone.usaWrestlingNumber = this.usaWrestlingNumber;
        clone.trackwrestlingId = this.trackwrestlingId;
        clone.location = this.location.clone();
        clone.highSchoolGradYear = this.highSchoolGradYear;
        clone.age = this.age;
        clone.user = this.user && this.user.clone();
        clone.hasModifyAccess = this.hasModifyAccess;
        clone.skillLevelStars = this.skillLevelStars;
        clone.disableAutoMerge = this.disableAutoMerge;

        if (this.dateOfBirth) {
            clone.dateOfBirth = new Date(this.dateOfBirth.valueOf());
        }

        if (this.teams) {
            clone.teams = this.teams.map((x: IdentityTeam) => x.clone());
        }

        if (this.bodyAssessments) {
            clone.bodyAssessments = this.bodyAssessments.map((x: IdentityBodyAssess) => x.clone(false));
            clone.bodyAssessments.forEach((ba: IdentityBodyAssess) => { ba.person = this; });
        }

        if (this.athletesAsParent) {
            clone.athletesAsParent = this.athletesAsParent.map((x: IdentityPerson) => x.clone());
        }

        if (this.parentsAsAthlete) {
            clone.parentsAsAthlete = this.parentsAsAthlete.map((x: IdentityPerson) => x.clone());
        }

        return clone;
    }

    /**
     * Serialize from a json api format
     * @param role
     * @param identitySportName
     * @param forceSaveAllValues boolean Force saving values even if they are not provided. This is used when managing
     * an athlete vs saving an athlete through registration. Through registration we don't force because we don't want
     * to wipe out any previously saved values.
     * @returns {string}
     */
    public serialize(role: string, identitySportName: string, forceSaveAllValues: boolean): any {
        const json: any = {};
        json.meta = {};
        json.data = {};
        json.data.type = 'person';
        json.data.id = this.id;
        json.data.attributes = {};
        json.data.attributes.firstName = this.firstName;
        json.data.attributes.lastName = this.lastName;
        json.data.relationships = {};

        if (this.gender || forceSaveAllValues) {
            json.data.attributes.gender = this.gender;
        }

        if (this.email || forceSaveAllValues) {
            json.data.attributes.email = this.email;
        }

        if (forceSaveAllValues) {
            json.data.attributes.disableAutoMerge = this.disableAutoMerge;
        }

        if (this.highSchoolGradYear || forceSaveAllValues) {
            json.data.attributes.highSchoolGradYear =
                this.highSchoolGradYear ? parseInt(this.highSchoolGradYear.toString(), null) : null;
        }

        if (this.user && forceSaveAllValues) {
            json.data.relationships.user = { data: { type: 'user', id: this.user.id } };
        }

        if (this.skillLevelStars || forceSaveAllValues) {
            json.data.attributes.sport = {};
            json.data.attributes.sport[identitySportName] = {};
            json.data.attributes.sport[identitySportName].skillLevelStars = null;

            if (this.skillLevelStars) {
                json.data.attributes.sport[identitySportName].skillLevelStars =
                    parseInt(this.skillLevelStars.toString(), null);
            }
        }

        if (this.usaWrestlingNumber || forceSaveAllValues) {
            json.data.relationships.organizationPerson = {
                data: {
                    type: 'organizationPerson',
                    id: null,
                    attributes: {
                        organizationId: IdentityOrganization.ID_USA_WRESTLING,
                        identifier: this.usaWrestlingNumber?.toString() || null,
                    },
                },
            };
        }

        if (this.dateOfBirth || forceSaveAllValues) {
            json.data.attributes.dateOfBirth = this.dateOfBirth ? format(this.dateOfBirth, DateFormat.YYYYMMDD) : null;
        }

        if (this.location || forceSaveAllValues) {
            json.data.attributes.location = this.location?.serialize() || null;
        }

        json.meta.role = role;

        return json;
    }

    /**
     * Deserialize from json api format
     */
    public deserialize(json: any, included: any[], identitySportName: string): IdentityPerson {
        const data: any = json['data'] || json;
        const attrs: any = data['attributes'];
        const meta: any = json['meta'];
        const relationships: any[] = data['relationships'] || [];
        included = included || json.included || [];

        this.id = data.id;
        this.firstName = attrs.firstName;
        this.lastName = attrs.lastName;
        this.email = attrs.email;
        this.gender = attrs.gender;
        this.sport = attrs.sport;
        this.disableAutoMerge = attrs.disableAutoMerge;

        if (attrs['sport'] &&
            attrs['sport'][identitySportName]) {
            const identitySport: string = attrs['sport'][identitySportName];
            this.skillLevelStars = identitySport['skillLevelStars'] && parseInt(identitySport['skillLevelStars'], null);
        }

        if (attrs.dateOfBirth) {
            this.dateOfBirth = convertToDateTime(attrs.dateOfBirth);
        }

        this.location = (new Location()).deserialize(attrs.location);
        this.highSchoolGradYear = attrs.highSchoolGradYear;

        // Set the usa wrestling number by looking through the organizationPeople
        const orgPeople: any[] = get(data, 'relationships.organizationPeople.data') || [];
        orgPeople.forEach((x: any) => {
            const item: any = included.filter((i: any) => i.id === x.id)[0];

            if (item) {
                const orgPerson: IdentityOrganizationPerson = (new IdentityOrganizationPerson()).deserialize(item);
                if (orgPerson.isUsaWrestling() && orgPerson.identifier) {
                    this.usaWrestlingNumber = parseInt(orgPerson.identifier, null);
                }
                if (orgPerson.isTrackwrestling() && orgPerson.identifier) {
                    this.trackwrestlingId = parseInt(orgPerson.identifier, null);
                }
            }
        });

        const teamsAsAthleteList: any[] = get(data, 'relationships.teamsAsAthlete.data') || [];
        teamsAsAthleteList.forEach((x: any) => {
            const item: any = included.filter((i: any) => i.id === x.id)[0];

            if (item) {
                this.teams.push((new IdentityTeam()).deserialize(item, included, identitySportName));
            }
        });

        const bodyAssessmentLinks: Object = relationships['bodyAssessments'];
        if (bodyAssessmentLinks &&
            included &&
            bodyAssessmentLinks['data']) {
            this.bodyAssessments = [];

            for (const bodyAssessmentLink of bodyAssessmentLinks['data']) {
                const bodyAssessment: Object = included.filter((i: Object) => i['id'] === bodyAssessmentLink['id'])[0];
                if (bodyAssessment) {
                    this.bodyAssessments.push((new IdentityBodyAssess()).deserialize(bodyAssessment, included));
                }
            }
        }

        if (meta && meta.userPermissions) {
            this.hasModifyAccess = meta.userPermissions.hasModifyAccess;
        }

        if (attrs.userId) {
            this.user = new IdentityUser(attrs.userId);

            // Fill User from relationships if it exists
            const user: any = included.filter((i: any) => i.type === 'user' && i.id === attrs.userId)[0];

            if (user) {
                this.user = (new IdentityUser(attrs.userId)).deserialize(user, included);
            }
        }

        const athletesAsParentList: any[] = get(data, 'relationships.athletesAsParent.data') || [];
        athletesAsParentList.forEach((x: any) => {
            const item: any = included.filter((i: any) => i.id === x.id)[0];

            if (item) {
                this.athletesAsParent.push((new IdentityPerson()).deserialize(item, included, identitySportName));
            } else if (x.id === this.id) {
                this.athletesAsParent.push(this.clone());
            }
        });

        const parentsAsAthleteList: any[] = get(data, 'relationships.parentsAsAthlete.data') || [];
        parentsAsAthleteList.forEach((x: any) => {
            const item: any = included.filter((i: any) => i.id === x.id)[0];

            if (item) {
                this.parentsAsAthlete.push((new IdentityPerson()).deserialize(item, included, identitySportName));
            } else if (x.id === this.id) {
                this.parentsAsAthlete.push(this.clone());
            }
        });

        return this;
    }
}
